import { getAudioCtx } from './audioCore';
// ═══════════════════════════════════════════════════════
//  CINEMATIC BEAST AUDIO — Deep Heavy-Bass Roars
//  Obsidian theme: every sound must FEEL heavy and dark
//  Falcon = Sharp Piercing High-Frequency Screech
//  All others = Deep Cinematic Roar with Heavy Bass
// ═══════════════════════════════════════════════════════

const audioCtx = () => getAudioCtx();

const playSound = (setup) => {
  try {
    const ctx = audioCtx();
    setup(ctx);
  } catch (e) { /* silent fail */ }
};

// Shared: Creates a deep rumbling sub-bass layer (the "heavy" in heavy bass)
const addSubBass = (ctx, t, freq = 35, dur = 1.5, vol = 0.6) => {
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.type = 'sine';
  osc.frequency.setValueAtTime(freq, t);
  osc.frequency.exponentialRampToValueAtTime(freq * 0.6, t + dur);
  gain.gain.setValueAtTime(vol, t);
  gain.gain.setValueAtTime(vol * 0.9, t + dur * 0.3);
  gain.gain.exponentialRampToValueAtTime(0.01, t + dur);
  osc.connect(gain); gain.connect(ctx.destination);
  osc.start(t); osc.stop(t + dur);
};

// Shared: Creates cinematic noise rumble (earthquake texture)
const addCinematicRumble = (ctx, t, dur = 1.2, vol = 0.35) => {
  const buf = ctx.createBuffer(1, ctx.sampleRate * dur, ctx.sampleRate);
  const d = buf.getChannelData(0);
  for (let i = 0; i < d.length; i++) {
    d[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / d.length, 1.0);
  }
  const src = ctx.createBufferSource(); src.buffer = buf;
  const lp = ctx.createBiquadFilter(); lp.type = 'lowpass'; lp.frequency.value = 200;
  const gain = ctx.createGain();
  gain.gain.setValueAtTime(vol, t);
  gain.gain.exponentialRampToValueAtTime(0.01, t + dur);
  src.connect(lp); lp.connect(gain); gain.connect(ctx.destination);
  src.start(t); src.stop(t + dur);
};

export const animalSounds = {
  // ─── LEOPARD: Rasping cinematic growl + low purr-rumble + jungle ambience ───
  fashion: () => playSound((ctx) => {
    const t = ctx.currentTime;

    // 1) Primary rasping growl — sawtooth with a tremolo for raspiness
    const osc = ctx.createOscillator();
    const g = ctx.createGain();
    osc.type = 'sawtooth';
    osc.frequency.setValueAtTime(70, t);
    osc.frequency.exponentialRampToValueAtTime(105, t + 0.15);
    osc.frequency.setValueAtTime(95, t + 0.5);
    osc.frequency.exponentialRampToValueAtTime(48, t + 1.6);
    g.gain.setValueAtTime(0.0, t);
    g.gain.linearRampToValueAtTime(0.6, t + 0.1);
    g.gain.exponentialRampToValueAtTime(0.01, t + 1.6);
    const lp = ctx.createBiquadFilter(); lp.type = 'lowpass'; lp.frequency.value = 420;
    osc.connect(lp); lp.connect(g); g.connect(ctx.destination);
    osc.start(t); osc.stop(t + 1.6);

    // Tremolo — gives the growl its rasping vibrato
    const trem = ctx.createOscillator();
    const tremG = ctx.createGain();
    trem.type = 'sine'; trem.frequency.value = 22;
    tremG.gain.value = 14;          // ±14 Hz wobble
    trem.connect(tremG); tremG.connect(osc.frequency);
    trem.start(t); trem.stop(t + 1.4);

    // 2) Sustained low purr — sine drone behind the growl
    const purr = ctx.createOscillator();
    const pg = ctx.createGain();
    purr.type = 'sine'; purr.frequency.value = 42;
    pg.gain.setValueAtTime(0.0, t);
    pg.gain.linearRampToValueAtTime(0.28, t + 0.4);
    pg.gain.linearRampToValueAtTime(0.22, t + 1.2);
    pg.gain.exponentialRampToValueAtTime(0.01, t + 2.0);
    purr.connect(pg); pg.connect(ctx.destination);
    purr.start(t); purr.stop(t + 2.0);

    // 3) Jungle ambience — soft high-frequency cricket-noise wash (very faint)
    const buf = ctx.createBuffer(1, ctx.sampleRate * 1.8, ctx.sampleRate);
    const d = buf.getChannelData(0);
    for (let i = 0; i < d.length; i += 1) d[i] = (Math.random() * 2 - 1) * 0.5;
    const noise = ctx.createBufferSource(); noise.buffer = buf;
    const bp = ctx.createBiquadFilter(); bp.type = 'bandpass'; bp.frequency.value = 4800; bp.Q.value = 1.4;
    const ng = ctx.createGain();
    ng.gain.setValueAtTime(0.0, t); ng.gain.linearRampToValueAtTime(0.05, t + 0.6);
    ng.gain.exponentialRampToValueAtTime(0.01, t + 1.8);
    noise.connect(bp); bp.connect(ng); ng.connect(ctx.destination);
    noise.start(t); noise.stop(t + 1.8);

    addSubBass(ctx, t, 30, 1.6, 0.6);
    addCinematicRumble(ctx, t, 1.4, 0.35);
  }),

  // ─── GAZELLE: Quick light hoofbeats + soft bleat + warm savanna breeze ───
  food: () => playSound((ctx) => {
    const t = ctx.currentTime;

    // 1) Soft bleat — gentle pitched call (the gazelle's signature)
    const bleat = ctx.createOscillator();
    const bg = ctx.createGain();
    bleat.type = 'triangle';
    bleat.frequency.setValueAtTime(520, t);
    bleat.frequency.exponentialRampToValueAtTime(720, t + 0.12);
    bleat.frequency.exponentialRampToValueAtTime(420, t + 0.6);
    bg.gain.setValueAtTime(0.0, t);
    bg.gain.linearRampToValueAtTime(0.32, t + 0.06);
    bg.gain.exponentialRampToValueAtTime(0.01, t + 0.7);
    const bbp = ctx.createBiquadFilter(); bbp.type = 'bandpass'; bbp.frequency.value = 900; bbp.Q.value = 1.2;
    bleat.connect(bbp); bbp.connect(bg); bg.connect(ctx.destination);
    bleat.start(t); bleat.stop(t + 0.7);

    // Light bleat vibrato
    const v = ctx.createOscillator(); const vg = ctx.createGain();
    v.type = 'sine'; v.frequency.value = 12; vg.gain.value = 18;
    v.connect(vg); vg.connect(bleat.frequency);
    v.start(t); v.stop(t + 0.6);

    // 2) Quick 6 light hoofbeats — staccato sine taps, rhythmic
    const beats = [0.0, 0.13, 0.27, 0.42, 0.58, 0.74];
    beats.forEach((off, i) => {
      const o = ctx.createOscillator(); const og = ctx.createGain();
      o.type = 'sine'; o.frequency.value = 110 - i * 4;     // slight pitch descent
      og.gain.setValueAtTime(0.4, t + off + 0.7);
      og.gain.exponentialRampToValueAtTime(0.01, t + off + 0.78);
      o.connect(og); og.connect(ctx.destination);
      o.start(t + off + 0.7); o.stop(t + off + 0.8);
    });

    // 3) Warm savanna breeze — soft pink-noise pad
    const buf = ctx.createBuffer(1, ctx.sampleRate * 1.6, ctx.sampleRate);
    const d = buf.getChannelData(0);
    for (let i = 0; i < d.length; i += 1) d[i] = (Math.random() * 2 - 1) * 0.4;
    const noise = ctx.createBufferSource(); noise.buffer = buf;
    const lp = ctx.createBiquadFilter(); lp.type = 'lowpass'; lp.frequency.value = 800;
    const ng = ctx.createGain();
    ng.gain.setValueAtTime(0.0, t); ng.gain.linearRampToValueAtTime(0.13, t + 0.5);
    ng.gain.exponentialRampToValueAtTime(0.01, t + 1.6);
    noise.connect(lp); lp.connect(ng); ng.connect(ctx.destination);
    noise.start(t); noise.stop(t + 1.6);

    addSubBass(ctx, t + 0.7, 38, 0.8, 0.35);
  }),

  // ─── FALCON: SHARP PIERCING HIGH-FREQUENCY SCREECH ───
  explore: () => playSound((ctx) => {
    const t = ctx.currentTime;
    // Primary screech — aggressive high saw
    const osc = ctx.createOscillator();
    const g = ctx.createGain();
    osc.type = 'sawtooth';
    osc.frequency.setValueAtTime(1800, t);
    osc.frequency.exponentialRampToValueAtTime(3500, t + 0.08);
    osc.frequency.setValueAtTime(3200, t + 0.15);
    osc.frequency.exponentialRampToValueAtTime(2600, t + 0.5);
    osc.frequency.exponentialRampToValueAtTime(1800, t + 0.9);
    osc.frequency.exponentialRampToValueAtTime(1200, t + 1.2);
    g.gain.setValueAtTime(0.25, t);
    g.gain.linearRampToValueAtTime(0.38, t + 0.08);
    g.gain.setValueAtTime(0.35, t + 0.2);
    g.gain.exponentialRampToValueAtTime(0.01, t + 1.2);
    const hp = ctx.createBiquadFilter(); hp.type = 'highpass'; hp.frequency.value = 1200;
    osc.connect(hp); hp.connect(g); g.connect(ctx.destination);
    osc.start(t); osc.stop(t + 1.2);
    // Echo screech — delayed, thinner
    const o2 = ctx.createOscillator(); const g2 = ctx.createGain();
    o2.type = 'sawtooth';
    o2.frequency.setValueAtTime(2800, t + 0.35);
    o2.frequency.exponentialRampToValueAtTime(1600, t + 0.75);
    g2.gain.setValueAtTime(0.12, t + 0.35);
    g2.gain.exponentialRampToValueAtTime(0.01, t + 0.75);
    o2.connect(hp); o2.start(t + 0.35); o2.stop(t + 0.8);
    // Cinematic wind — adds weight
    const buf = ctx.createBuffer(1, ctx.sampleRate * 0.6, ctx.sampleRate);
    const d = buf.getChannelData(0);
    for (let i = 0; i < d.length; i++) d[i] = (Math.random() * 2 - 1) * 0.4;
    const src = ctx.createBufferSource(); src.buffer = buf;
    const bp = ctx.createBiquadFilter(); bp.type = 'bandpass'; bp.frequency.value = 5000; bp.Q.value = 0.8;
    const wg = ctx.createGain(); wg.gain.setValueAtTime(0.1, t); wg.gain.exponentialRampToValueAtTime(0.01, t + 0.6);
    src.connect(bp); bp.connect(wg); wg.connect(ctx.destination);
    src.start(t); src.stop(t + 0.6);
    // Even the falcon gets a bass underline
    addSubBass(ctx, t, 45, 0.8, 0.3);
  }),

  // ─── PHOENIX: Mythic ascending firebird cry + crystalline chime + ember crackle ───
  souls: () => playSound((ctx) => {
    const t = ctx.currentTime;

    // 1) Cry — soaring high-frequency vocal pad (the firebird)
    const cry = ctx.createOscillator();
    const cg = ctx.createGain();
    cry.type = 'sawtooth';
    cry.frequency.setValueAtTime(420, t);
    cry.frequency.exponentialRampToValueAtTime(880, t + 0.4);
    cry.frequency.exponentialRampToValueAtTime(1200, t + 0.8);
    cry.frequency.exponentialRampToValueAtTime(640, t + 1.5);
    cg.gain.setValueAtTime(0.0, t);
    cg.gain.linearRampToValueAtTime(0.32, t + 0.2);
    cg.gain.linearRampToValueAtTime(0.28, t + 1.0);
    cg.gain.exponentialRampToValueAtTime(0.01, t + 1.8);
    const cbp = ctx.createBiquadFilter(); cbp.type = 'bandpass'; cbp.frequency.value = 1400; cbp.Q.value = 1.0;
    cry.connect(cbp); cbp.connect(cg); cg.connect(ctx.destination);
    cry.start(t); cry.stop(t + 1.8);

    // 2) Crystalline shimmer — three bell-tone oscillators stacked
    [{f: 1320, off: 0.2}, {f: 1760, off: 0.32}, {f: 2640, off: 0.48}].forEach(({f, off}) => {
      const bell = ctx.createOscillator(); const bg = ctx.createGain();
      bell.type = 'sine'; bell.frequency.value = f;
      bg.gain.setValueAtTime(0.0, t + off);
      bg.gain.linearRampToValueAtTime(0.16, t + off + 0.08);
      bg.gain.exponentialRampToValueAtTime(0.01, t + off + 1.3);
      bell.connect(bg); bg.connect(ctx.destination);
      bell.start(t + off); bell.stop(t + off + 1.4);
    });

    // 3) Ember crackle — pink-noise burst at 800-2000Hz, several short pops
    [0.25, 0.55, 0.9, 1.1].forEach((off) => {
      const buf = ctx.createBuffer(1, ctx.sampleRate * 0.08, ctx.sampleRate);
      const d = buf.getChannelData(0);
      for (let i = 0; i < d.length; i += 1) d[i] = (Math.random() * 2 - 1);
      const n = ctx.createBufferSource(); n.buffer = buf;
      const bp = ctx.createBiquadFilter(); bp.type = 'bandpass'; bp.frequency.value = 1500; bp.Q.value = 1.6;
      const ng = ctx.createGain();
      ng.gain.setValueAtTime(0.18, t + off);
      ng.gain.exponentialRampToValueAtTime(0.01, t + off + 0.08);
      n.connect(bp); bp.connect(ng); ng.connect(ctx.destination);
      n.start(t + off); n.stop(t + off + 0.1);
    });

    // 4) Bass swell — the mythic foundation
    addSubBass(ctx, t, 36, 1.8, 0.55);
    addCinematicRumble(ctx, t, 1.5, 0.35);
  }),

  // ─── GORILLA: Hoot-pant series + chest pounds + jungle reverb ───
  sporty: () => playSound((ctx) => {
    const t = ctx.currentTime;

    // 1) Hoot-pant series — 4 rapid breathy "whoo-whoo" calls (signature gorilla)
    const hoots = [0.0, 0.18, 0.36, 0.54];
    hoots.forEach((off) => {
      const o = ctx.createOscillator();
      const og = ctx.createGain();
      o.type = 'sine';
      o.frequency.setValueAtTime(160, t + off);
      o.frequency.exponentialRampToValueAtTime(220, t + off + 0.08);
      o.frequency.exponentialRampToValueAtTime(130, t + off + 0.16);
      og.gain.setValueAtTime(0.0, t + off);
      og.gain.linearRampToValueAtTime(0.4, t + off + 0.03);
      og.gain.exponentialRampToValueAtTime(0.01, t + off + 0.18);
      const lp = ctx.createBiquadFilter(); lp.type = 'lowpass'; lp.frequency.value = 700;
      o.connect(lp); lp.connect(og); og.connect(ctx.destination);
      o.start(t + off); o.stop(t + off + 0.2);
    });

    // 2) Climax roar after the hoots — explosive
    const roar = ctx.createOscillator();
    const rg = ctx.createGain();
    roar.type = 'sawtooth';
    roar.frequency.setValueAtTime(58, t + 0.75);
    roar.frequency.exponentialRampToValueAtTime(110, t + 0.95);
    roar.frequency.exponentialRampToValueAtTime(40, t + 1.6);
    rg.gain.setValueAtTime(0.0, t + 0.75);
    rg.gain.linearRampToValueAtTime(0.7, t + 0.9);
    rg.gain.exponentialRampToValueAtTime(0.01, t + 1.7);
    const rlp = ctx.createBiquadFilter(); rlp.type = 'lowpass'; rlp.frequency.value = 380;
    roar.connect(rlp); rlp.connect(rg); rg.connect(ctx.destination);
    roar.start(t + 0.75); roar.stop(t + 1.7);

    // 3) Heavy chest pounds — 4 deep slaps in rhythm with the roar
    [0.85, 1.05, 1.2, 1.35].forEach((off) => {
      const o = ctx.createOscillator(); const og = ctx.createGain();
      o.type = 'sine'; o.frequency.value = 36;
      og.gain.setValueAtTime(0.7, t + off);
      og.gain.exponentialRampToValueAtTime(0.01, t + off + 0.1);
      o.connect(og); og.connect(ctx.destination);
      o.start(t + off); o.stop(t + off + 0.12);
    });

    // 4) Jungle reverb tail — filtered pink noise
    const buf = ctx.createBuffer(1, ctx.sampleRate * 1.8, ctx.sampleRate);
    const d = buf.getChannelData(0);
    for (let i = 0; i < d.length; i += 1) d[i] = (Math.random() * 2 - 1) * 0.35;
    const noise = ctx.createBufferSource(); noise.buffer = buf;
    const bp = ctx.createBiquadFilter(); bp.type = 'bandpass'; bp.frequency.value = 350; bp.Q.value = 0.8;
    const ng = ctx.createGain();
    ng.gain.setValueAtTime(0.0, t); ng.gain.linearRampToValueAtTime(0.12, t + 0.8);
    ng.gain.exponentialRampToValueAtTime(0.01, t + 1.8);
    noise.connect(bp); bp.connect(ng); ng.connect(ctx.destination);
    noise.start(t); noise.stop(t + 1.8);

    addSubBass(ctx, t + 0.7, 26, 1.4, 0.7);
    addCinematicRumble(ctx, t, 1.6, 0.4);
  }),

  // ─── STALLION: Sharp whinny trill + galloping hoofs + wind whoosh ───
  success: () => playSound((ctx) => {
    const t = ctx.currentTime;

    // 1) Sharp opening whinny — trill at top of register
    const whin = ctx.createOscillator();
    const wg = ctx.createGain();
    whin.type = 'sawtooth';
    whin.frequency.setValueAtTime(280, t);
    whin.frequency.exponentialRampToValueAtTime(680, t + 0.12);
    whin.frequency.setValueAtTime(640, t + 0.45);
    whin.frequency.exponentialRampToValueAtTime(420, t + 0.7);
    whin.frequency.exponentialRampToValueAtTime(180, t + 1.0);
    wg.gain.setValueAtTime(0.0, t);
    wg.gain.linearRampToValueAtTime(0.42, t + 0.08);
    wg.gain.exponentialRampToValueAtTime(0.01, t + 1.0);
    const wbp = ctx.createBiquadFilter(); wbp.type = 'bandpass'; wbp.frequency.value = 900; wbp.Q.value = 1.4;
    whin.connect(wbp); wbp.connect(wg); wg.connect(ctx.destination);
    whin.start(t); whin.stop(t + 1.0);

    // Whinny trill — fast vibrato on the sustain
    const trem = ctx.createOscillator(); const tg = ctx.createGain();
    trem.type = 'sine'; trem.frequency.value = 24; tg.gain.value = 38;
    trem.connect(tg); tg.connect(whin.frequency);
    trem.start(t + 0.1); trem.stop(t + 0.7);

    // 2) Galloping hoofbeats — 8 heavy pounds in a 1-2-3 / 1-2-3 gallop rhythm
    const gallop = [
      0.55, 0.62, 0.70,    // first 3-beat cluster
      0.92, 0.99, 1.07,    // second 3-beat cluster
      1.30, 1.40,          // closing emphasis
    ];
    gallop.forEach((off, i) => {
      const o = ctx.createOscillator(); const og = ctx.createGain();
      o.type = 'sine'; o.frequency.value = 50 + (i % 3) * 8;
      og.gain.setValueAtTime(0.55 - i * 0.03, t + off);
      og.gain.exponentialRampToValueAtTime(0.01, t + off + 0.1);
      o.connect(og); og.connect(ctx.destination);
      o.start(t + off); o.stop(t + off + 0.12);
    });

    // 3) Wind whoosh — sense of motion
    const buf = ctx.createBuffer(1, ctx.sampleRate * 1.6, ctx.sampleRate);
    const d = buf.getChannelData(0);
    for (let i = 0; i < d.length; i += 1) d[i] = (Math.random() * 2 - 1) * 0.4;
    const noise = ctx.createBufferSource(); noise.buffer = buf;
    const bp = ctx.createBiquadFilter(); bp.type = 'bandpass'; bp.frequency.value = 2200; bp.Q.value = 0.7;
    const ng = ctx.createGain();
    ng.gain.setValueAtTime(0.0, t);
    ng.gain.linearRampToValueAtTime(0.1, t + 0.4);
    ng.gain.linearRampToValueAtTime(0.08, t + 1.0);
    ng.gain.exponentialRampToValueAtTime(0.01, t + 1.6);
    noise.connect(bp); bp.connect(ng); ng.connect(ctx.destination);
    noise.start(t); noise.stop(t + 1.6);

    addSubBass(ctx, t + 0.5, 32, 1.2, 0.55);
    addCinematicRumble(ctx, t + 0.4, 1.0, 0.3);
  }),

  // ─── LION: Inhale + thundering roar + chest grunts — the deepest bass ───
  legacy: () => playSound((ctx) => {
    const t = ctx.currentTime;

    // 1) Inhale breath — the heavy intake before the roar
    const inhaleBuf = ctx.createBuffer(1, ctx.sampleRate * 0.45, ctx.sampleRate);
    const ib = inhaleBuf.getChannelData(0);
    for (let i = 0; i < ib.length; i += 1) {
      ib[i] = (Math.random() * 2 - 1) * (i / ib.length) * 0.4;  // ramps in
    }
    const inhale = ctx.createBufferSource(); inhale.buffer = inhaleBuf;
    const ihp = ctx.createBiquadFilter(); ihp.type = 'highpass'; ihp.frequency.value = 400;
    const ig = ctx.createGain();
    ig.gain.setValueAtTime(0.12, t);
    ig.gain.exponentialRampToValueAtTime(0.01, t + 0.45);
    inhale.connect(ihp); ihp.connect(ig); ig.connect(ctx.destination);
    inhale.start(t); inhale.stop(t + 0.45);

    // 2) Main roar — ultra-deep sawtooth sweep (offset to start AFTER inhale)
    const roarStart = t + 0.35;
    const osc = ctx.createOscillator();
    const g = ctx.createGain();
    osc.type = 'sawtooth';
    osc.frequency.setValueAtTime(48, roarStart);
    osc.frequency.exponentialRampToValueAtTime(105, roarStart + 0.18);
    osc.frequency.exponentialRampToValueAtTime(82, roarStart + 0.5);
    osc.frequency.exponentialRampToValueAtTime(55, roarStart + 1.0);
    osc.frequency.exponentialRampToValueAtTime(28, roarStart + 1.9);
    g.gain.setValueAtTime(0.0, roarStart);
    g.gain.linearRampToValueAtTime(0.72, roarStart + 0.2);
    g.gain.linearRampToValueAtTime(0.55, roarStart + 0.7);
    g.gain.exponentialRampToValueAtTime(0.01, roarStart + 1.9);
    const lp = ctx.createBiquadFilter(); lp.type = 'lowpass'; lp.frequency.value = 280;
    osc.connect(lp); lp.connect(g); g.connect(ctx.destination);
    osc.start(roarStart); osc.stop(roarStart + 1.9);

    addSubBass(ctx, roarStart, 24, 1.9, 0.75);
    addCinematicRumble(ctx, roarStart, 1.7, 0.5);

    // 3) Chest grunts — punctuating impacts during the roar
    [0.1, 0.28, 0.46, 0.6].forEach((off) => {
      const o = ctx.createOscillator(); const og = ctx.createGain();
      o.type = 'sine'; o.frequency.value = 42;
      og.gain.setValueAtTime(0.55, roarStart + off);
      og.gain.exponentialRampToValueAtTime(0.01, roarStart + off + 0.12);
      o.connect(og); og.connect(ctx.destination);
      o.start(roarStart + off); o.stop(roarStart + off + 0.15);
    });

    // 4) Closing exhale grunt — a low resonant tail
    const exhale = ctx.createOscillator(); const eg = ctx.createGain();
    exhale.type = 'sine'; exhale.frequency.value = 38;
    eg.gain.setValueAtTime(0.0, roarStart + 1.5);
    eg.gain.linearRampToValueAtTime(0.4, roarStart + 1.6);
    eg.gain.exponentialRampToValueAtTime(0.01, roarStart + 2.0);
    exhale.connect(eg); eg.connect(ctx.destination);
    exhale.start(roarStart + 1.5); exhale.stop(roarStart + 2.0);
  }),

  // ─── CRANE: A regal, brassy bugling trumpet-call with a reverberant tail ───
  art: () => playSound((ctx) => {
    const t = ctx.currentTime;

    // Main bugle — sawtooth trumpet with a pitched up-sweep, hold, then taper.
    const osc = ctx.createOscillator();
    const g = ctx.createGain();
    osc.type = 'sawtooth';
    osc.frequency.setValueAtTime(380, t);
    osc.frequency.exponentialRampToValueAtTime(620, t + 0.18);   // pitch rises (the "ka-")
    osc.frequency.setValueAtTime(620, t + 0.55);                  // sustain (the "-rrrrr-")
    osc.frequency.exponentialRampToValueAtTime(420, t + 1.1);    // soft descent (the "-ooooo")
    g.gain.setValueAtTime(0.0, t);
    g.gain.linearRampToValueAtTime(0.55, t + 0.08);
    g.gain.linearRampToValueAtTime(0.5, t + 0.7);
    g.gain.exponentialRampToValueAtTime(0.01, t + 1.4);
    // Brass coloring — bandpass around 1.2kHz to give it that crane brassiness
    const bp = ctx.createBiquadFilter();
    bp.type = 'bandpass'; bp.frequency.value = 1200; bp.Q.value = 1.6;
    osc.connect(bp); bp.connect(g); g.connect(ctx.destination);
    osc.start(t); osc.stop(t + 1.4);

    // Higher overtone for shimmer (4th harmonic, lower amplitude)
    const ov = ctx.createOscillator();
    const og = ctx.createGain();
    ov.type = 'triangle';
    ov.frequency.setValueAtTime(760, t);
    ov.frequency.exponentialRampToValueAtTime(1240, t + 0.2);
    ov.frequency.exponentialRampToValueAtTime(840, t + 1.1);
    og.gain.setValueAtTime(0.0, t);
    og.gain.linearRampToValueAtTime(0.22, t + 0.12);
    og.gain.exponentialRampToValueAtTime(0.01, t + 1.3);
    ov.connect(og); og.connect(ctx.destination);
    ov.start(t); ov.stop(t + 1.3);

    // A faint flutter (rapid pitch wobble in the middle — the trilly crane vibrato)
    const lfo = ctx.createOscillator();
    const lfoGain = ctx.createGain();
    lfo.type = 'sine';
    lfo.frequency.value = 16;          // 16 Hz wobble
    lfoGain.gain.value = 22;           // ±22 Hz of pitch modulation
    lfo.connect(lfoGain);
    lfoGain.connect(osc.frequency);
    lfo.start(t + 0.3); lfo.stop(t + 0.8);

    // Reverberant tail — a soft noise burst delayed, evokes wetland echo
    const buf = ctx.createBuffer(1, ctx.sampleRate * 0.6, ctx.sampleRate);
    const data = buf.getChannelData(0);
    for (let i = 0; i < data.length; i += 1) {
      data[i] = (Math.random() * 2 - 1) * (1 - i / data.length) * 0.18;
    }
    const noise = ctx.createBufferSource();
    noise.buffer = buf;
    const ng = ctx.createGain();
    ng.gain.setValueAtTime(0.0, t);
    ng.gain.linearRampToValueAtTime(0.25, t + 1.0);
    ng.gain.exponentialRampToValueAtTime(0.01, t + 1.8);
    const hp = ctx.createBiquadFilter();
    hp.type = 'highpass'; hp.frequency.value = 700;
    noise.connect(hp); hp.connect(ng); ng.connect(ctx.destination);
    noise.start(t + 0.9); noise.stop(t + 1.8);
  }),
};
